import { useState } from 'react'

// 1.2.2: the ten icons now come from the Figma export instead of being drawn here by hand.
import {
  ArvIcon,
  FlipIcon,
  ScoreIcon,
  TemplatesIcon,
  PdfIcon,
  DealTrackingIcon,
  PropertyDashboardIcon,
  PropertyDataIcon,
  CustomTagsIcon,
  NotesIcon,
} from './workflowIcons'

const IconWrap = ({ children }) => (
  <div className="w-10 h-10 rounded-lg bg-white/5 shadow-[0_2px_8px_rgba(0,0,0,0.1)] flex items-center justify-center shrink-0">
    {children}
  </div>
)

const TABS = [
  {
    id: 'analysis',
    label: 'Deal Analysis',
    tools: [
      { name: 'ARV Analysis', Icon: ArvIcon },
      { name: 'Flip & BRRRR Projection', Icon: FlipIcon },
      { name: 'Deal Scoring', Icon: ScoreIcon },
      { name: 'Templates', Icon: TemplatesIcon },
      { name: 'PDF Reports', Icon: PdfIcon, badge: 'COMING SOON' },
    ],
  },
  {
    id: 'pipeline',
    label: 'Deal Pipeline',
    tools: [
      { name: 'Deal Tracking', Icon: DealTrackingIcon },
      { name: 'Property Dashboard', Icon: PropertyDashboardIcon },
      { name: 'Property Data & History', Icon: PropertyDataIcon },
      { name: 'Custom Tags', Icon: CustomTagsIcon },
      { name: 'Notes', Icon: NotesIcon },
    ],
  },
]

// 13.08 set #4 D1.3: "Coming Soon" 12px right of the tool label, both breakpoints — split
// out of the icon/label gap-3/gap-4 so the badge distance is fixed at 12px regardless of
// that spacing, instead of inheriting whatever the icon-to-label gap happens to be.
const ToolItem = ({ name, Icon, badge }) => (
  <div className="flex items-center">
    <div className="flex items-center gap-3 md:gap-4">
      <IconWrap>
        <Icon />
      </IconWrap>
      <span className="text-[16px] leading-[24px] md:text-[20px] md:leading-[32px] font-bold text-white whitespace-nowrap">
        {name}
      </span>
    </div>
    {badge && (
      <span className="ml-3 h-[20px] px-2 rounded-[4px] bg-brand-primary text-white text-[10px] font-bold tracking-wide flex items-center shrink-0">
        {badge}
      </span>
    )}
  </div>
)

const Tools = () => {
  const [activeTab, setActiveTab] = useState('analysis')

  const tab = TABS.find((t) => t.id === activeTab)

  return (
    <section className="bg-brand-dark relative overflow-hidden">
      <div className="absolute -left-[40px] top-[206px] w-[300px] h-[300px] rounded-full bg-[radial-gradient(circle,_rgba(76,188,24,0.18),_transparent_70%)] pointer-events-none" />
      <div className="absolute right-[260px] top-[373px] w-[200px] h-[200px] rounded-full bg-[radial-gradient(circle,_rgba(76,188,24,0.12),_transparent_70%)] pointer-events-none" />

      {/* M1.2.1: 32px from the hero image to this heading — the hero drops its own bottom
          padding on mobile and the whole gap lives here.
          M1.3.1: 24/40 below, the other half of the 48/80 gap to "Everything You Need".
          13.08 set #4 D1.1: desktop now mirrors that same mobile pattern — this pt is the
          space between the dark/light section boundary and the heading, on the dark side.
          14.08 followup: a same-day attempt raised this to 150px to chase Микола's "60px"
          number, but that number is card-bottom-to-boundary, not boundary-to-heading — this
          padding sits after the boundary, so no value here can add space before it. Reverted
          to 60px; the actual image-to-boundary gap now lives in Hero.jsx's own bottom padding. */}
      <div className="relative z-10 max-w-canvas mx-auto px-6 md:px-0 pt-[32px] md:pt-[60px] pb-[24px] md:pb-[40px] flex flex-col items-center">
        <h2 className="text-[28px] leading-[36px] md:text-[40px] md:leading-[48px] font-extrabold text-white text-center">
          The Complete
          <br />
          <span className="text-brand-primary">Deal Analysis Workflow</span>
        </h2>

        <div className="mt-[37px] flex flex-wrap justify-center gap-2 max-w-[1000px]">
          {TABS.map(({ id, label }) => {
            const active = activeTab === id
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`h-[48px] px-6 rounded-full border text-[16px] font-semibold leading-6 text-brand-greenSec transition-colors ${
                  active
                    ? 'bg-[#213b13] border-[#2a7a05]'
                    : 'bg-brand-dark border-[#3b4238] hover:border-[#2a7a05]'
                }`}
              >
                {label}
              </button>
            )
          })}
        </div>

        <div className="mt-6 w-full max-w-[820px] rounded-3xl bg-white/5 border border-white/10 p-5 md:p-8">
          {/* D1.3.1, read off the section export: 16/24 weight 700 desktop, 14 weight 800
              mobile, both #8A9A85 and both `letter-spacing: 0em`. The 0.14em tracking here was
              ours — at 16px it added 2.24px per character, which is what pushed the label wide
              enough to collide with the icon row Микола saw. */}
          <p className="text-center text-[14px] md:text-[16px] leading-[24px] font-extrabold md:font-bold tracking-normal text-brand-placeholder uppercase">
            Included Tools
          </p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
            {tab.tools.map(({ name, Icon, badge }) => (
              <ToolItem key={name} name={name} Icon={Icon} badge={badge} />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Tools
